import type { ITheme } from '@xterm/xterm';

// A selectable color scheme. `xterm` drives the terminal buffer (background,
// foreground, cursor, selection + the full 16-colour ANSI palette); `ui` drives
// the surrounding app chrome via CSS custom properties (see applyChrome).
export interface UiPalette {
  bg: string; // page / terminal background
  panel: string; // headers, cards, toolbars
  border: string; // hairline separators
  text: string; // primary foreground
  accent: string; // primary action / highlight colour
}

export interface Theme {
  id: string;
  name: string;
  xterm: ITheme;
  ui: UiPalette;
}

// THEMES is the curated, ordered list. The first entry is the default and must
// reproduce the original ZionWave look so existing installs see no change.
// Palettes below use the canonical published values for each scheme.
export const THEMES: Theme[] = [
  {
    id: 'zionwave-dark',
    name: 'ZionWave Dark',
    xterm: {
      background: '#0b0e14',
      foreground: '#e6e9ef',
      cursor: '#4c8bf5',
      cursorAccent: '#0b0e14',
      selectionBackground: '#232a3a',
      black: '#11151f',
      red: '#f85149',
      green: '#3fb950',
      yellow: '#d29922',
      blue: '#4c8bf5',
      magenta: '#bc8cff',
      cyan: '#39c5cf',
      white: '#b1bac4',
      brightBlack: '#6e7681',
      brightRed: '#ff7b72',
      brightGreen: '#56d364',
      brightYellow: '#e3b341',
      brightBlue: '#79a8ff',
      brightMagenta: '#d2a8ff',
      brightCyan: '#56d4dd',
      brightWhite: '#f0f6fc',
    },
    ui: { bg: '#0b0e14', panel: '#11151f', border: '#232a3a', text: '#e6e9ef', accent: '#4c8bf5' },
  },
  {
    id: 'dracula',
    name: 'Dracula',
    xterm: {
      background: '#282a36',
      foreground: '#f8f8f2',
      cursor: '#f8f8f2',
      cursorAccent: '#282a36',
      selectionBackground: '#44475a',
      black: '#21222c',
      red: '#ff5555',
      green: '#50fa7b',
      yellow: '#f1fa8c',
      blue: '#bd93f9',
      magenta: '#ff79c6',
      cyan: '#8be9fd',
      white: '#f8f8f2',
      brightBlack: '#6272a4',
      brightRed: '#ff6e6e',
      brightGreen: '#69ff94',
      brightYellow: '#ffffa5',
      brightBlue: '#d6acff',
      brightMagenta: '#ff92df',
      brightCyan: '#a4ffff',
      brightWhite: '#ffffff',
    },
    ui: { bg: '#282a36', panel: '#21222c', border: '#44475a', text: '#f8f8f2', accent: '#bd93f9' },
  },
  {
    id: 'solarized-dark',
    name: 'Solarized Dark',
    xterm: {
      background: '#002b36',
      foreground: '#839496',
      cursor: '#93a1a1',
      cursorAccent: '#002b36',
      selectionBackground: '#073642',
      black: '#073642',
      red: '#dc322f',
      green: '#859900',
      yellow: '#b58900',
      blue: '#268bd2',
      magenta: '#d33682',
      cyan: '#2aa198',
      white: '#eee8d5',
      brightBlack: '#002b36',
      brightRed: '#cb4b16',
      brightGreen: '#586e75',
      brightYellow: '#657b83',
      brightBlue: '#839496',
      brightMagenta: '#6c71c4',
      brightCyan: '#93a1a1',
      brightWhite: '#fdf6e3',
    },
    ui: { bg: '#002b36', panel: '#073642', border: '#586e75', text: '#93a1a1', accent: '#268bd2' },
  },
  {
    id: 'nord',
    name: 'Nord',
    xterm: {
      background: '#2e3440',
      foreground: '#d8dee9',
      cursor: '#d8dee9',
      cursorAccent: '#2e3440',
      selectionBackground: '#434c5e',
      black: '#3b4252',
      red: '#bf616a',
      green: '#a3be8c',
      yellow: '#ebcb8b',
      blue: '#81a1c1',
      magenta: '#b48ead',
      cyan: '#88c0d0',
      white: '#e5e9f0',
      brightBlack: '#4c566a',
      brightRed: '#bf616a',
      brightGreen: '#a3be8c',
      brightYellow: '#ebcb8b',
      brightBlue: '#81a1c1',
      brightMagenta: '#b48ead',
      brightCyan: '#8fbcbb',
      brightWhite: '#eceff4',
    },
    ui: { bg: '#2e3440', panel: '#3b4252', border: '#434c5e', text: '#d8dee9', accent: '#88c0d0' },
  },
  {
    id: 'gruvbox-dark',
    name: 'Gruvbox Dark',
    xterm: {
      background: '#282828',
      foreground: '#ebdbb2',
      cursor: '#ebdbb2',
      cursorAccent: '#282828',
      selectionBackground: '#504945',
      black: '#282828',
      red: '#cc241d',
      green: '#98971a',
      yellow: '#d79921',
      blue: '#458588',
      magenta: '#b16286',
      cyan: '#689d6a',
      white: '#a89984',
      brightBlack: '#928374',
      brightRed: '#fb4934',
      brightGreen: '#b8bb26',
      brightYellow: '#fabd2f',
      brightBlue: '#83a598',
      brightMagenta: '#d3869b',
      brightCyan: '#8ec07c',
      brightWhite: '#ebdbb2',
    },
    ui: { bg: '#282828', panel: '#3c3836', border: '#504945', text: '#ebdbb2', accent: '#fabd2f' },
  },
  {
    id: 'solarized-light',
    name: 'Solarized Light',
    xterm: {
      background: '#fdf6e3',
      foreground: '#657b83',
      cursor: '#586e75',
      cursorAccent: '#fdf6e3',
      selectionBackground: '#eee8d5',
      black: '#073642',
      red: '#dc322f',
      green: '#859900',
      yellow: '#b58900',
      blue: '#268bd2',
      magenta: '#d33682',
      cyan: '#2aa198',
      white: '#eee8d5',
      brightBlack: '#002b36',
      brightRed: '#cb4b16',
      brightGreen: '#586e75',
      brightYellow: '#657b83',
      brightBlue: '#839496',
      brightMagenta: '#6c71c4',
      brightCyan: '#93a1a1',
      brightWhite: '#fdf6e3',
    },
    ui: { bg: '#fdf6e3', panel: '#eee8d5', border: '#93a1a1', text: '#657b83', accent: '#268bd2' },
  },
];

// THEME_KEY is the localStorage slot holding the chosen theme id.
const THEME_KEY = 'zw_theme';

// THEME_CHANGE_EVENT fires on `window` after the theme changes so an open
// terminal can re-theme itself live (see terminal-view.ts).
export const THEME_CHANGE_EVENT = 'zw-theme-change';

// selectedTheme resolves a stored id to a Theme, falling back to the default
// (first) theme when the id is missing or unknown. Pure: pass `stored`
// explicitly in tests; the default argument reads localStorage in the app.
export function selectedTheme(stored: string | null = localStorage.getItem(THEME_KEY)): Theme {
  return THEMES.find((t) => t.id === stored) ?? THEMES[0];
}

// setTheme persists the chosen id and notifies listeners (open terminals) so the
// change applies live. Chrome is updated separately via applyChrome.
export function setTheme(id: string): void {
  localStorage.setItem(THEME_KEY, id);
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: id }));
}

// relativeLuminance returns a 0–1 brightness for a #rrggbb colour, used to pick
// the correct `color-scheme` (form controls, scrollbars) for light vs dark UIs.
function relativeLuminance(hex: string): number {
  const v = hex.replace('#', '');
  const r = parseInt(v.slice(0, 2), 16) / 255;
  const g = parseInt(v.slice(2, 4), 16) / 255;
  const b = parseInt(v.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// applyChrome writes the five UI palette colours as CSS custom properties on the
// root element. styles.css derives every surface/border/text shade from these,
// so the whole app chrome re-themes from one place. `root` is injectable for
// tests; it defaults to <html>.
export function applyChrome(theme: Theme, root: HTMLElement = document.documentElement): void {
  const { ui } = theme;
  root.style.setProperty('--zw-bg', ui.bg);
  root.style.setProperty('--zw-panel', ui.panel);
  root.style.setProperty('--zw-border', ui.border);
  root.style.setProperty('--zw-text', ui.text);
  root.style.setProperty('--zw-accent', ui.accent);
  root.style.colorScheme = relativeLuminance(ui.bg) > 0.5 ? 'light' : 'dark';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', ui.bg);
}
